import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { v2API, v3API } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  ArrowLeft, Play, Loader2, CheckCircle2, XCircle, AlertCircle,
  Download, FileCheck, RefreshCw, FlaskConical
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  pass: {
    label: 'Pass',
    color: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    icon: CheckCircle2,
  },
  fail: {
    label: 'Fail',
    color: 'text-red-700 bg-red-50 border-red-200',
    icon: XCircle,
  },
  pending: {
    label: 'Not tested',
    color: 'text-zinc-600 bg-zinc-50 border-zinc-200',
    icon: AlertCircle,
  },
};

const formatBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export default function AdminTemplateQA() {
  const navigate = useNavigate();
  const { user, isSuperadmin } = useAuth();
  const { builder: builderParam } = useParams();

  // builderParam expected to be 'v2' or 'v3'; default to v3
  const initialBuilder = builderParam === 'v2' ? 'v2' : 'v3';
  const [builder, setBuilder] = useState(initialBuilder);
  const [results, setResults] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateMeta, setTemplateMeta] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState(null);
  const [runningAll, setRunningAll] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isSuperadmin()) {
      navigate('/dashboard');
    }
  }, [user, isSuperadmin, navigate]);

  // Keep URL in sync with builder selection
  useEffect(() => {
    const newPath = `/admin/template-qa/${builder}`;
    if (window.location.pathname !== newPath) {
      window.history.replaceState(null, '', newPath);
    }
  }, [builder]);

  const api = builder === 'v2' ? v2API : v3API;

  const loadResults = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getQAResults();
      setResults(data.results || []);
      setTemplates(data.templates || []);
      setTemplateMeta(data.templateMeta || []);
    } catch (e) {
      console.error('Failed to load QA results', e);
      toast.error('Failed to load QA results');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const handleRunOne = async (templateId) => {
    setRunningId(templateId);
    try {
      const r = await api.runQA(templateId, user?.email || 'admin');
      if (r.status === 'pass') {
        toast.success(`${templateId}: PASS`);
      } else {
        toast.error(`${templateId}: FAIL`);
      }
      await loadResults();
    } catch (e) {
      console.error('QA run failed', e);
      toast.error(`QA failed for ${templateId}`);
    } finally {
      setRunningId(null);
    }
  };

  const handleRunAll = async () => {
    setRunningAll(true);
    try {
      // Run sequentially so we don't overload Playwright
      for (const tid of templates) {
        setRunningId(tid);
        try {
          await api.runQA(tid, user?.email || 'admin');
        } catch (err) {
          console.error(`QA failed for ${tid}`, err);
        }
      }
      toast.success(`Ran QA on ${templates.length} templates`);
      await loadResults();
    } finally {
      setRunningId(null);
      setRunningAll(false);
    }
  };

  // Merge templateMeta with results so we always show every template, tested or not
  const rows = templates.map((tid) => {
    const meta = (templateMeta || []).find(
      (m) => (m.id || m.templateId || m) === tid
    ) || {};
    const result = results.find((r) => r.templateId === tid);
    return {
      templateId: tid,
      name: meta.name || result?.templateName || tid,
      category: meta.category || result?.category || '',
      result,
    };
  });

  const passCount = rows.filter((r) => r.result?.status === 'pass').length;
  const failCount = rows.filter((r) => r.result?.status === 'fail').length;
  const untestedCount = rows.filter((r) => !r.result).length;

  return (
    <div className="min-h-screen bg-zinc-50/50" data-testid="admin-qa-page">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              data-testid="qa-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-zinc-200" />
            <div className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-purple-600" />
              <h1 className="text-lg font-semibold text-zinc-900">
                Template QA
              </h1>
            </div>
          </div>

          {/* Builder switch */}
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-zinc-200 p-1 bg-zinc-50">
              <button
                type="button"
                className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                  builder === 'v2'
                    ? 'bg-white text-zinc-900 shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-700'
                }`}
                onClick={() => setBuilder('v2')}
                data-testid="qa-switch-v2"
              >
                V2 Templates
              </button>
              <button
                type="button"
                className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                  builder === 'v3'
                    ? 'bg-white text-zinc-900 shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-700'
                }`}
                onClick={() => setBuilder('v3')}
                data-testid="qa-switch-v3"
              >
                V3 Templates
              </button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadResults}
              disabled={loading}
              data-testid="qa-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleRunAll}
              disabled={runningAll || templates.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="qa-run-all-btn"
            >
              {runningAll ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Run all
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wide">Total templates</div>
              <div className="text-2xl font-semibold text-zinc-900 mt-1" data-testid="qa-stat-total">
                {templates.length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-emerald-600 uppercase tracking-wide">Passing</div>
              <div className="text-2xl font-semibold text-emerald-700 mt-1" data-testid="qa-stat-pass">
                {passCount}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-red-600 uppercase tracking-wide">Failing</div>
              <div className="text-2xl font-semibold text-red-700 mt-1" data-testid="qa-stat-fail">
                {failCount}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wide">Untested</div>
              <div className="text-2xl font-semibold text-zinc-700 mt-1" data-testid="qa-stat-untested">
                {untestedCount}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Results table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileCheck className="w-4 h-4 text-purple-600" />
              {builder.toUpperCase()} Template Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-12 flex items-center justify-center text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
              </div>
            ) : rows.length === 0 ? (
              <div className="py-12 text-center text-zinc-500" data-testid="qa-empty">
                No templates found for {builder.toUpperCase()}.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wide text-zinc-500">
                      <th className="py-2 pr-3">Template</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3">Clean PDF</th>
                      <th className="py-2 pr-3">Watermark PDF</th>
                      <th className="py-2 pr-3">Marker</th>
                      <th className="py-2 pr-3">Last run</th>
                      <th className="py-2 pr-3">By</th>
                      <th className="py-2 pr-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ templateId, name, category, result }) => {
                      const status = result?.status || 'pending';
                      const meta = STATUS_META[status];
                      const StatusIcon = meta.icon;
                      const isRunning = runningId === templateId;
                      const previewBase = process.env.REACT_APP_BACKEND_URL + '/api';
                      const previewUrl = builder === 'v3'
                        ? null // V3 needs a stored CV id for direct preview
                        : null;

                      return (
                        <tr
                          key={templateId}
                          className="border-b border-zinc-100 hover:bg-zinc-50/50"
                          data-testid={`qa-row-${templateId}`}
                        >
                          <td className="py-3 pr-3">
                            <div className="font-medium text-zinc-900">{name}</div>
                            <div className="text-xs text-zinc-500 font-mono">{templateId}</div>
                            {category && (
                              <div className="text-xs text-zinc-400 mt-0.5">{category}</div>
                            )}
                          </td>
                          <td className="py-3 pr-3">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-xs font-medium ${meta.color}`}
                              data-testid={`qa-status-${templateId}`}
                            >
                              <StatusIcon className="w-3.5 h-3.5" />
                              {meta.label}
                            </span>
                          </td>
                          <td className="py-3 pr-3 text-zinc-700">
                            {result?.cleanExportOk ? (
                              <span className="text-emerald-700">
                                ✓ {formatBytes(result.cleanPdfSize || 0)}
                              </span>
                            ) : result ? (
                              <span className="text-red-600">✗ Failed</span>
                            ) : (
                              <span className="text-zinc-400">—</span>
                            )}
                          </td>
                          <td className="py-3 pr-3 text-zinc-700">
                            {result?.watermarkExportOk ? (
                              <span className="text-emerald-700">
                                ✓ {formatBytes(result.watermarkPdfSize || 0)}
                              </span>
                            ) : result ? (
                              <span className="text-red-600">✗ Failed</span>
                            ) : (
                              <span className="text-zinc-400">—</span>
                            )}
                          </td>
                          <td className="py-3 pr-3">
                            {result?.templateMarkerPresent ? (
                              <span className="text-emerald-700 text-xs">✓ present</span>
                            ) : result ? (
                              <span className="text-red-600 text-xs">✗ missing</span>
                            ) : (
                              <span className="text-zinc-400 text-xs">—</span>
                            )}
                          </td>
                          <td className="py-3 pr-3 text-xs text-zinc-600">
                            {formatDate(result?.lastQADate)}
                          </td>
                          <td className="py-3 pr-3 text-xs text-zinc-600 truncate max-w-[160px]">
                            {result?.qaBy || '—'}
                          </td>
                          <td className="py-3 pr-3 text-right">
                            <Button
                              size="sm"
                              variant={result ? 'outline' : 'default'}
                              onClick={() => handleRunOne(templateId)}
                              disabled={isRunning || runningAll}
                              data-testid={`qa-run-${templateId}`}
                            >
                              {isRunning ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <>
                                  <Play className="w-3.5 h-3.5 mr-1" />
                                  Run
                                </>
                              )}
                            </Button>
                            {previewUrl && (
                              <a
                                href={previewUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-2 inline-flex"
                              >
                                <Button size="sm" variant="ghost">
                                  <Download className="w-3.5 h-3.5" />
                                </Button>
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-xs text-zinc-400 px-1">
          QA generates a fixed sample CV, renders the template, exports both clean
          and watermark PDFs through Playwright, and verifies the template marker
          (<code className="font-mono text-zinc-500">data-{builder}-template</code>)
          is present in the HTML.
        </div>
      </main>
    </div>
  );
}
